import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getLegalContent } from '../legal';

const KEY = 'cb_cookie_consent';
const TS_KEY = 'cb_cookie_consent_ts';
const MAX_AGE_MS = 365 * 24 * 3600 * 1000;

function needsChoice() {
  try {
    const v = localStorage.getItem(KEY);
    if (!v) return true;
    const ts = parseInt(localStorage.getItem(TS_KEY) || '0', 10);
    return Date.now() - ts > MAX_AGE_MS;
  } catch {
    return true;
  }
}

const S = {
  wrap: {
    position: 'fixed', bottom: 16, left: 16, right: 16, zIndex: 9990,
    maxWidth: 520, margin: '0 auto', padding: '16px 18px',
    background: 'rgba(18,18,20,0.96)', backdropFilter: 'blur(16px)',
    border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14,
    boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
  },
  text: { fontSize: 13, lineHeight: 1.55, color: '#C9CFD8', margin: '0 0 12px' },
  link: { color: '#60A5FA', textDecoration: 'underline' },
  row: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  accept: {
    padding: '9px 18px', borderRadius: 999, border: 'none', cursor: 'pointer',
    background: '#3B82F6', color: '#FFFFFF', fontSize: 13, fontWeight: 600,
  },
  essential: {
    padding: '9px 18px', borderRadius: 999, cursor: 'pointer',
    background: 'transparent', color: '#A3A3A3', fontSize: 13, fontWeight: 500,
    border: '1px solid rgba(255,255,255,0.18)',
  },
};

const CookieConsentBanner = () => {
  const { currentLanguage } = useLanguage();
  const [visible, setVisible] = useState(needsChoice);
  const [bottomOffset, setBottomOffset] = useState(16);

  useEffect(() => {
    const onReset = () => setVisible(true);
    window.addEventListener('cb-cookie-consent-reset', onReset);
    return () => window.removeEventListener('cb-cookie-consent-reset', onReset);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const measure = () => {
      const bar = document.querySelector('[data-testid="lang-suggestion-banner"]');
      setBottomOffset(bar ? bar.getBoundingClientRect().height + 24 : 16);
    };
    measure();
    const id = setInterval(measure, 1000);
    return () => clearInterval(id);
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    const el = document.querySelector('[data-testid="cookie-consent-banner"]');
    const h = el ? el.getBoundingClientRect().height : 0;
    const prev = document.body.style.paddingBottom;
    if (h) document.body.style.paddingBottom = `${h + 32}px`;
    return () => { document.body.style.paddingBottom = prev; };
  }, [visible, bottomOffset]);

  if (!visible) return null;
  const c = getLegalContent(currentLanguage).common.consent;

  const choose = (value) => {
    try {
      localStorage.setItem(KEY, value);
      localStorage.setItem(TS_KEY, String(Date.now()));
    } catch {}
    if (value === 'all' && window.cbEnableAnalytics) window.cbEnableAnalytics();
    setVisible(false);
  };

  return (
    <div style={{ ...S.wrap, bottom: bottomOffset }} role="dialog" aria-live="polite" aria-label={c.accept} data-testid="cookie-consent-banner">
      <p style={S.text}>
        {c.text}{' '}
        <a href="/cookies" style={S.link} data-testid="cookie-consent-policy-link">{c.more}</a>
      </p>
      <div style={S.row}>
        <button style={S.accept} onClick={() => choose('all')} data-testid="cookie-consent-accept">{c.accept}</button>
        <button style={S.essential} onClick={() => choose('essential')} data-testid="cookie-consent-essential">{c.essential}</button>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
